// simuiform.ts ↔ game/SimUI.cpp — the SimUIForm HUD slice (the Form arms:
// Init/SizeAndPositionEverything, the OnPaint* frame hookup, Update's display
// refresh, OnUpdateMapInvalidate's incremental gob invalidation) and the render-side
// Gob::Invalidate (game/GameObjects.cpp:267 — m_trcBoundingLast lives here, client-side).
// (The C++ MiniMapControl radar panel was ported here too; Phase 2 of the forms.ini
// elimination replaced it with the DOM <canvas> radar — dom/ingame/minimap-*.ts.)
//
// The INPUT slice of the same C++ class (HitTestGob/MoveOrAttackOrSelect/
// SetSelectionTargets/...) is already ported in ui/fingerhandler.ts; this form
// COMPOSES that object (deps.sui) the way simdraw.ts composes the sim — the
// C++ globals it reaches arrive through it (gsim/ggobm/gpplrLocal/gevm) or
// through SimUIHudDeps seams (gpdisp/gtimm/ggame/gpiniForms-era forms).
// The lockstep arms (OnTimer/QueueUpdateMessage/ProcessUpdateMessage/
// RunUpdatesNow/SendUpdateResult) are the PROVEN sim driver
// (sim/src/sim/driver.ts); the MP callback arms (OnNetMessage/OnLagNotify/
// WaitForm/chat) are M3-later.

import { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import { ksideAttackMoveTint } from '../gfx/tbitmap.ts';
import { GetColor, iclr } from '../gfx/palette.ts';
import type { UpdateMap, TRectSmall } from '../gfx/updatemap.ts';
import type { TileMap } from '../gfx/tilemap.ts';
import type { ClientFogDraw } from '../gfx/fogdraw.ts';
import { ktcMax, kpcMax } from '../gfx/coords.ts';
import type { Event } from './event.ts';
import {
  penDownEvent,
  penUpEvent,
  penMoveEvent,
  penHoverEvent,
  penHoldEvent,
  penDownEvent2,
  penMoveEvent2,
  penUpEvent2,
  runUpdatesNowEvent,
  kfRedrawDirty,
} from './event.ts';
import { Rect } from './rect.ts';
import type { RectLike } from './rect.ts';
import { DrawBorder, kfFrmDemandPen2, kfFrmVisible } from './uikit.ts';
import type { PaintLayer, FormMgr } from '../render/paintengine.ts';
import { beginProjectileInterp } from '../render/projectile-interp.ts';
import type { SimUIModel } from '../dom/ingame/simui-view.ts';
import { TimerMgr } from './timer.ts';
import type { SimUIForm as SimUIFormInput, PenHandler } from './fingerhandler.ts';
import { FingerHandler, SimulationClearGobSelection } from './fingerhandler.ts';
import { StylusHandler, WRectSet } from './stylushandler.ts';
import type { InputUIForm } from './inputui.ts';
import { AlertState } from './alertstate.ts';
import type { GobDrawContext } from '../render/gobdraw.ts';
import { creditsPips } from '../render/gobdraw.ts';
import {
  drawBackground,
  drawFog,
  simulationDraw,
  gobClippingBounds,
  type SimView,
} from '../render/simdraw.ts';
import { dbgOverlay, drawDebugPaths, drawDebugTargetLines } from '../render/debugoverlay.ts';
import { Animation, AnimData, kfAniDone } from '../../../sim/src/anim/animation.ts';
import { TcFromWc } from '../../../sim/src/math/coords.ts';
import { itofx, fxtoi, mulfx, fracfx, divfx } from '../../../sim/src/math/fix.ts';
import { s8, s16 } from '../../../sim/src/math/int32.ts';
import {
  kfGobRedraw,
  kfGobSelected,
  kfGobTransitioningToVisible,
} from '../../../sim/src/gob/gob.ts';
import { kgtProcessor, kgtWarehouse } from '../../../sim/src/gob/gobtypes.ts';
import { kfPlrObserver } from '../../../sim/src/player/side.ts';
import { kfCtVisibleAtStart } from '../../../sim/src/trigger/triggermgr.ts';

// game/res.h
export const kidfSimUI = 1036; // res.h:33
export const kidcFps = 1004; // res.h:166
export const kidcObjective = 1005; // res.h:167
export const kidcCountdown = 1006; // res.h:168
export const kidcChat = 1904; // res.h:263

/** gfDragSelecting/gfLassoSelection/gwrcSelection/s_awptSelection — the
 *  stylus-handler drag-selection globals (game/SimUI.cpp:42-47). The finger UI
 *  never raises fDragSelecting; the StylusHandler that does is M3-later. */
export interface DragSelectionState {
  fDragSelecting: boolean;
  fLassoSelection: boolean;
  wrcSelection: RectLike; // gwrcSelection (world coords)
  awptSelection: { wx: number; wy: number }[]; // s_awptSelection
  cwptSelection: number; // s_cwptSelection
  wptSelect1: { wx: number; wy: number }; // s_wptSelect1 (SimUI.cpp:43)
  wptSelect2: { wx: number; wy: number }; // s_wptSelect2
}

export function defaultDragSelectionState(): DragSelectionState {
  return {
    fDragSelecting: false,
    fLassoSelection: false,
    wrcSelection: { left: 0, top: 0, right: 0, bottom: 0 },
    awptSelection: [],
    cwptSelection: 0,
    wptSelect1: { wx: 0, wy: 0 },
    wptSelect2: { wx: 0, wy: 0 },
  };
}

/** The injected globals the HUD slice reaches (see the header comment). */
export interface SimUIHudDeps {
  /** the SimUIForm INPUT slice (ui/fingerhandler.ts) — carries ctx/sim/gctx/
   *  evm/host plus the move-target state both slices share. */
  sui: SimUIFormInput;
  /** gsim.GetLevel()->GetTileMap() — terrain offsets + the mini tileset. */
  tmap: TileMap;
  /** the fog/galaxite renderer (gsim.DrawBackground/DrawFog's client arm). */
  fog: ClientFogDraw;
  /** gtimm — the presentation timer list. */
  gtimm: TimerMgr;
  /** g_panidMoveTarget (game/MobileUnit.cpp:126, "movetarget.anir"). */
  anidMoveTarget: AnimData;
  /** HostGetTickCount (1/100s) — the FPS-stat clock. */
  HostGetTickCount: () => number;
  /** Renderer seam: surface factory for the minimap's owned dib (a GPU target
   *  texture in the C++; a GL surface under the WebGL2 renderer). Default =
   *  the CPU reference surface. */
  createSurface?: (cx: number, cy: number) => CpuDibBitmap;
  /** ggame.GetInputUIForm() (SimUI.cpp:1202 + MiniMapControl::Init). */
  getInputUIForm: () => InputUIForm | null;
  /** gpdisp->GetSafeAreas (SimUI.cpp:363). */
  getSafeAreas?: () => RectLike;
  /** gfShowFPS. */
  fShowFPS?: boolean;
  /** The in-game OVERLAY-TEXT-LABEL DOM seam (Phase 5).
   *  When set, the SimUIForm SUPPRESSES the canvas paint of its 4 text labels
   *  (objective / alert / countdown / FPS) — their kfCtlVisible + GetText() stay
   *  canonical so the DOM reads them uniformly — and pushes a model here each frame
   *  (the end of Update). main.ts mounts a SimUIView over the live canvas (a
   *  pointer-events:none overlay — map gestures fall through). Absent (a headless harness)
   *  ⇒ no UI shown. DISPLAY-only — no sim/command/wire touch. */
  domSimUI?: { update(m: SimUIModel): void };
  /** gevm.SetPenEventInterval (SimUI.cpp:1240). */
  setPenEventInterval?: (ct: number) => void;
  /** m_pchatter->ShowChat() (SimUI.cpp:1611) — MP chat, M3-later. */
  showChat?: () => void;
  /** RunUpdatesNow (SimUI.cpp:636) — lockstep catch-up; driver-owned. */
  runUpdatesNow?: () => void;
  /** the stylus drag-selection globals (inactive under the finger UI). */
  drag?: DragSelectionState;
  /** gfLassoSelection/gfMouseMode (game.cpp:51-52; the WEB build forces
   *  gfMouseMode true — game.cpp:296-301) — SetUIType's handler choice
   *  (SimUI.cpp:171/219-232). Absent = both false = kuitFinger. */
  options?: { fLassoSelection: boolean; fMouseMode: boolean };
}

//
// Gob::Invalidate — render-side (game/GameObjects.cpp:267-320). The
// m_trcBoundingLast field is RENDER-only state stashed on the sim gob (the C++
// ctor init is GameObjects.cpp:195-198); gptrcMapOpaque/gpupdSim arrive as
// parameters.
//

interface GobWithBounds {
  GetFlags(): number;
  m_trcBoundingLast?: TRectSmall;
}

export function gobInvalidate(
  gctx: GobDrawContext,
  pupd: UpdateMap,
  pgob: GobWithBounds,
  gptrcMapOpaque: TRectSmall | null,
): boolean {
  const c = gctx.coords;

  // Get the clipping bounds, the rect that surrounds the visible pixels of
  // this gob

  const rc = gobClippingBounds(gctx, pgob as never);

  // Change into a tile rect

  const trcNew: TRectSmall = {
    txLeft: s8(c.TcFromPc(rc.left)),
    tyTop: s8(c.TcFromPc(rc.top)),
    txRight: 0,
    tyBottom: 0,
  };
  const xT = Math.min(rc.right + c.gcxTile - 1, kpcMax);
  trcNew.txRight = s8(c.TcFromPc(xT));
  const yT = Math.min(rc.bottom + c.gcyTile - 1, kpcMax);
  trcNew.tyBottom = s8(c.TcFromPc(yT));

  // GameObjects.cpp:195-198 (ctor init) — first touch of the render field
  if (pgob.m_trcBoundingLast === undefined) {
    pgob.m_trcBoundingLast = { txLeft: 127, tyTop: 127, txRight: -128, tyBottom: -128 };
  }
  const trcLast = pgob.m_trcBoundingLast;

  // If the gob was transitioning from off screen to on screen, use the
  // current invalidation rect. Otherwise Union with the old rect so that the
  // screen redraws the old location and the new location

  if (pgob.GetFlags() & kfGobTransitioningToVisible) {
    trcLast.txLeft = trcNew.txLeft;
    trcLast.tyTop = trcNew.tyTop;
    trcLast.txRight = trcNew.txRight;
    trcLast.tyBottom = trcNew.tyBottom;
  } else {
    // ht.h:883-894 TRectSmall::Union
    if (!(trcNew.txLeft >= trcNew.txRight || trcNew.tyTop >= trcNew.tyBottom)) {
      if (trcLast.txLeft > trcNew.txLeft) trcLast.txLeft = trcNew.txLeft;
      if (trcLast.tyTop > trcNew.tyTop) trcLast.tyTop = trcNew.tyTop;
      if (trcLast.txRight < trcNew.txRight) trcLast.txRight = trcNew.txRight;
      if (trcLast.tyBottom < trcNew.tyBottom) trcLast.tyBottom = trcNew.tyBottom;
    }
  }

  // Is this opaqued? If so nothing to do. Clip to edges of opaque rect; if all
  // inside return false

  if (gptrcMapOpaque !== null) {
    if (gptrcMapOpaque.txLeft <= trcLast.txLeft) {
      if (gptrcMapOpaque.txRight >= trcLast.txRight) {
        if (gptrcMapOpaque.tyTop <= trcLast.tyTop) {
          if (gptrcMapOpaque.tyBottom >= trcLast.tyBottom) {
            return false;
          }
        }
      }
    }
  }

  // Perform invalidation of what's visible

  pupd.InvalidateMapTileRect(trcLast);

  // Remember new

  pgob.m_trcBoundingLast = trcNew;
  return true;
}

//
// SimUIForm implementation (the HUD slice)
//

// HTML/DOM UI shell plan, Phase 3 (forms.ini elimination): the playfield HUD slice
// is NO LONGER a forms.ini-loaded Form — it is a renderer-owned PaintLayer the
// FormMgr's proven incremental engine drives through the PaintLayer members (the
// paint/scroll walk + pen-capture cooking are UNCHANGED). There is NO control tree:
// the 4 overlay text labels (objective/alert/countdown/FPS) flow from the canonical
// holders to the DOM SimUIView (Phase 1+5); the minimap is the DOM radar (Phase 2).
// The Form base fields/methods the engine reads (m_idf/m_wf/m_rc, GetId/GetFlags,
// GetRect/SetRect/Show/InvalidateRect, OnHitTest/OnKeyTest/BreakCapture/OnScroll,
// GetFormMgr) are provided here directly — no forms.ini geometry, no Form base.
export class SimUIForm implements PaintLayer {
  readonly deps: SimUIHudDeps;
  readonly drag: DragSelectionState;

  // --- PaintLayer identity/geometry (the engine reads these off m_apfrm) ---
  m_pfrmm: FormMgr | null = null;
  m_rc = new Rect();
  m_idf = kidfSimUI; // the cooked-event form id (CookPenEvent capture key)
  m_wf = 0; // kfFrmVisible (set in Init) | kfFrmDemandPen2

  /** m_ppenh (SimUI.cpp:171/219-232 — gfLassoSelection || gfMouseMode picks
   *  kuitStylus, else kuitFinger; the shipped WEB client runs kuitStylus). */
  m_ppenh!: PenHandler;
  /** m_aniMoveTarget (the sim-timing Animation; drawn via the client art). */
  m_aniMoveTarget = new Animation();

  // SimUI.cpp:153 static bool s_fReadyForPaint (single live instance)
  s_fReadyForPaint = false;

  // SimUI.cpp:33-38 file statics (FPS stats)
  gcPaint = 0; // gcPaint (game.cpp:2290 increments per painted frame)
  /** the previous paint's FindVisibleGobs list — OnUpdateMapInvalidate damages the
   *  last bounds of gobs that left the set (see the departed-gob arm there). */
  private m_apgobVisiblePrev: readonly unknown[] | null = null;
  private readonly m_setVisibleScratch = new Set<unknown>();
  private s_cfxFPS = 0;
  private s_tLastFPSUpdate = 0;
  private s_tLastPaint = 0; // Update's function-static (SimUI.cpp:1237)
  /** the last computed FPS readout text ("whole.fraction") — the CANONICAL fps
   *  holder (Phase 1). Written in the stats arm of Update; the SimUIView push
   *  reads it (Phase 3: there is no canvas FPS control). */
  private s_fpsText = '';

  // The CANONICAL in-game OVERLAY holders (HTML/DOM UI shell plan, Phase 1: the
  // forms.ini-elimination pass). The SINGLE source of truth for the alert + countdown
  // text/visibility, feeding the SimUIView push. (Phase 3): the canvas controls
  // these once mirrored are deleted — the holders feed the DOM only.
  /** the ALERT toast (AlertControl's text+4s-timer logic, sans Form/Control).
   *  Constructed with the SimUIForm's gtimm. ShowAlert routes here. */
  readonly alertState: AlertState;
  /** the COUNTDOWN timer text — host UpdateCountdownString writes it. */
  countdownText = '';
  /** the COUNTDOWN visibility — host ShowCountdownTimer toggles it; the
   *  kfCtVisibleAtStart init (Init) seeds it. */
  countdownVisible = false;

  // SimUI.cpp:17-18 gptrcMapOpaque/gtrcMapOpaque (map-relative TILE coords)
  gtrcMapOpaque: TRectSmall = { txLeft: 0, tyTop: 0, txRight: 0, tyBottom: 0 };
  gptrcMapOpaque: TRectSmall | null = null;

  /** the SimView the render arms consume (wx/wyView refreshed per use). */
  private view: SimView; // draw-time fog/terrain/gob view; rebuilt by RebindView() on a sim swap

  // game/SimUI.cpp:155-189 (ctor) — the HUD-side arms: paint gate + move
  // target + the pen handler. The MP fields (m_pcmdqServer/transport
  // callbacks/chatter) are driver/M3-later.
  constructor(deps: SimUIHudDeps) {
    this.deps = deps;
    this.drag = deps.drag ?? defaultDragSelectionState();

    // The canonical ALERT holder (Phase 1) — built with gtimm so its 4s timer is
    // byte-identical to the AlertControl it replaces. ShowAlert routes here.
    this.alertState = new AlertState(deps.gtimm);

    this.s_fReadyForPaint = false;
    this.drag.fDragSelecting = false; // gfDragSelecting = false
    this.drag.cwptSelection = 0; // s_cwptSelection = 0

    deps.sui.m_nStateMoveTarget = 0;

    // SetUIType(gfLassoSelection || gfMouseMode ? kuitStylus : kuitFinger)
    // (SimUI.cpp:171)
    this.SetUIType();

    this.view = this.buildView();

    // The input slice's host seams that belong to THIS object in the C++
    // (m_aniMoveTarget.Start, m_pfrmm->GetUpdateMap()->InvalidateTile —
    // simui.cpp:2342-2343). NOTE: sui.host.Scroll is NOT re-wired here — the
    // GameRenderer ctor already owns it (gpfrmmSim.Scroll + the sprite
    // ScrollDamage pass; m_pfrmm IS that same FormMgr), and a bare reassignment
    // here would silently drop the sprite damage under scoped invalidation.
    deps.sui.host.StartMoveTargetAnim = () => {
      this.m_aniMoveTarget.Start(0, 0, 0);
    };
    deps.sui.host.InvalidateTile = (tx: number, ty: number) => {
      this.m_pfrmm?.GetUpdateMap()?.InvalidateTile(tx, ty);
    };
  }

  private buildView(): SimView {
    const ctx = this.deps.sui.ctx;
    const dims = ctx.gobm.GetMapSize();
    return {
      gobm: ctx.gobm as unknown as SimView['gobm'],
      fogBytes: ctx.fogm!.GetMapPtr(),
      ctxMap: dims.ctx,
      ctyMap: dims.cty,
      trMapBytes: ctx.trmap!.GetMapPtr(),
      wxView: 0,
      wyView: 0,
      plrLocal: ctx.plrLocal as unknown as SimView['plrLocal'],
      apuntc: ctx.unitConsts as unknown as SimView['apuntc'],
    };
  }

  /** Rebuild the draw-time SimView after the input slice has been rebound to a NEW Sim (the replay
   *  viewer's backward-seek/fog-toggle). The fog/terrain/gob arrays are new identities, so the cached
   *  view that drawBackground/drawFog read must be rebuilt or the terrain renders the OLD sim's fog. */
  RebindView(): void {
    this.view = this.buildView();
  }

  /** the per-draw view refresh (gsim.GetViewPos reads inside the draw arms). */
  private refreshView(): SimView {
    this.view.wxView = this.deps.sui.sim.m_wxView;
    this.view.wyView = this.deps.sui.sim.m_wyView;
    return this.view;
  }

  // game/SimUI.cpp:255-337 — the HUD-slice Init, re-homed (Phase 3): no forms.ini
  // load, no control tree. The renderer calls this then AddLayer()s the layer. The
  // 4 overlay text labels live in the DOM (SimUIView); only the canonical holders
  // (alertState/countdownText/countdownVisible/s_fpsText + plrLocal.GetObjective())
  // remain here. The MP arms + the gtimm lockstep timer are the sim driver's.
  Init(pfrmm: FormMgr): boolean {
    // bind the layer to its FormMgr (AddLayer also sets m_pfrmm; set it early so
    // SizeAndPositionEverything below can read GetDib())
    this.m_pfrmm = pfrmm;

    // Init move target

    this.deps.sui.m_nStateMoveTarget = 0;
    this.m_aniMoveTarget.Init(this.deps.anidMoveTarget); // g_panidMoveTarget

    // visible from the start (the canvas Form was Show(true)'d at Init end)
    this.m_wf |= kfFrmVisible;

    this.SizeAndPositionEverything();

    // Seed the CANONICAL countdown-visibility holder (Phase 1) from the trigger
    // mgr's kfCtVisibleAtStart — the same SimUI.cpp:286 initial-visibility the C++
    // applied to the kidcCountdown control. The DOM SimUIView reads the holder.
    const pcdt = (
      this.deps.sui.sim as unknown as {
        GetLevel(): { GetTriggerMgr(): { GetCountdownTimer(): { m_wf: number } } } | null;
      }
    )
      .GetLevel()!
      .GetTriggerMgr()
      .GetCountdownTimer();
    this.countdownVisible = (pcdt.m_wf & kfCtVisibleAtStart) !== 0;

    // This layer wants pen2 events (multi-touch playfield select — the C++
    // SimUIForm set kfFrmDemandPen2; the FormMgr's StealCapturePen2 reads it)
    this.m_wf |= kfFrmDemandPen2;

    return true;
  }

  // game/SimUI.cpp:219-232 SetUIType — `delete m_ppenh; m_ppenh = new ...`;
  // the uit argument collapses into the SimUI.cpp:171 / GameOptions.cpp:461
  // choice both callers compute: gfLassoSelection || gfMouseMode -> kuitStylus.
  SetUIType(): void {
    const o = this.deps.options;
    // gfLassoSelection is ONE C++ global: the options storage feeds the drag
    // state the stylus arms + the rect/lasso painting read
    this.drag.fLassoSelection = o?.fLassoSelection ?? false;
    this.m_ppenh?.Dispose(); // delete m_ppenh
    const fStylus = this.drag.fLassoSelection || (o?.fMouseMode ?? false);
    // Mirror the input mode into the sim so the $iphone trigger sysvar (gamevar.ts)
    // resolves to the touch (multitouch) vs mouse variant. Lasso is a selection
    // STYLE, not a device, so $iphone tracks gfMouseMode alone. Default mouse
    // (fMouseMode absent ⇒ $iphone=0) preserves the option-less harness behavior.
    this.deps.sui.ctx.fTouchControls = !(o?.fMouseMode ?? true);
    if (fStylus) {
      this.m_ppenh = new StylusHandler({
        sui: this.deps.sui,
        drag: this.drag,
        invalidateDragSelection: () => this.InvalidateDragSelection(),
      });
    } else {
      this.m_ppenh = new FingerHandler(this.deps.sui);
    }
    // Keep the placement form's pass-through in sync with the active handler:
    // only the FingerHandler scrolls the playfield (so it needs clicks passed
    // through); the StylusHandler lets the placement form handle clicks
    // directly. Re-runs on a mouse-mode toggle (GameOptions.cpp:461). On the
    // first call (SimUIForm ctor) gpfrmPlace doesn't exist yet — GameSession
    // sets the initial state once BuildUI is built.
    this.deps.sui.host.GetPlaceForm()?.SetPassOnInput(!fStylus);
  }

  // ht.h:3760-3762
  GetPenHandler(): PenHandler {
    return this.m_ppenh;
  }

  // game/SimUI.cpp:2030-2038 — reset the drag-select statics + deselect all
  ClearSelection(): void {
    this.drag.wptSelect1.wx = 0;
    this.drag.wptSelect1.wy = 0;
    this.drag.wptSelect2.wx = this.drag.wptSelect1.wx;
    this.drag.wptSelect2.wy = this.drag.wptSelect1.wy;
    this.drag.cwptSelection = 0;
    WRectSet(this.drag.wrcSelection, this.drag.wptSelect1, this.drag.wptSelect2);
    SimulationClearGobSelection(this.deps.sui.ctx); // gsim.ClearGobSelection
  }

  // game/SimUI.cpp:339-346
  OnDisplayResize(): void {
    this.SizeAndPositionEverything();

    const wpt = { wx: 0, wy: 0 };
    this.deps.sui.GetViewPos(wpt);
    this.deps.sui.SetViewPos(wpt.wx, wpt.wy, true); // the fInit arm forces re-pin
  }

  // game/SimUI.cpp:350-404 — size the layer to cover the sim strip. The control
  // positioning (objective/countdown/fps/chat) is GONE — those labels are DOM now
  // (SimUIView, which owns its own layout). Only the layer rect (the full-bleed
  // playfield) remains, which the engine's opaque-rect/hit-test/paint walk reads.
  SizeAndPositionEverything(): void {
    const pbmSim = this.m_pfrmm!.GetDib();
    const sizSim = pbmSim.GetSize();
    const rcSim = new Rect();
    rcSim.Set(0, 0, sizSim.cx, sizSim.cy);
    this.SetRect(rcSim);
  }

  // game/SimUI.cpp:905-914 + form.cpp:604-622 — the layer's cooked-event dispatch.
  // The base Form.EventProc routed pen events to OnPenEvent and back-key to cancel;
  // the layer reproduces that pen routing (the playfield's whole input path) directly
  // — there is no control tree, so the only non-pen arm is the lockstep runUpdatesNow.
  EventProc(pevt: Event): boolean {
    switch (pevt.eType) {
      case penHoverEvent:
      case penDownEvent:
      case penMoveEvent:
      case penUpEvent:
      case penHoldEvent:
      case penDownEvent2:
      case penMoveEvent2:
      case penUpEvent2:
        return this.OnPenEvent(pevt);
      case runUpdatesNowEvent:
        this.deps.runUpdatesNow?.(); // RunUpdatesNow (the lockstep driver's)
        return true;
    }
    return false;
  }

  // game/SimUI.cpp:1173-1259 — the HUD-display arm of Update (the STRESS arm
  // is compiled out; m_pfrmWaitingForAllPlayers is MP/M3-later)
  Update(): void {
    const sui = this.deps.sui;

    // Advance the move target while it's visible

    if (sui.m_nStateMoveTarget === 1) {
      this.m_aniMoveTarget.Advance(1);
      if (this.m_aniMoveTarget.GetFlags() & kfAniDone) sui.m_nStateMoveTarget = 2;
    }

    // Have the InputUI update its displays (e.g., credits, power)

    const pfrmInputUI = this.deps.getInputUIForm();
    pfrmInputUI!.Update();

    // (Phase 3) the objective text is read straight from plrLocal.GetObjective()
    // into the DOM push below — there is no canvas kidcObjective label anymore.

    // Calc stats

    const tCurrent = this.deps.HostGetTickCount();
    if (this.s_tLastFPSUpdate === 0) this.s_tLastFPSUpdate = tCurrent;
    if (tCurrent - this.s_tLastFPSUpdate >= 100) {
      const fx1 = itofx(this.gcPaint * 100);
      const fx2 = itofx(tCurrent - this.s_tLastFPSUpdate);
      if (fx2 === 0) {
        this.s_cfxFPS = 0;
      } else {
        this.s_cfxFPS = s16(divfx(fx1, fx2)); // (fix)divfx32
      }
      const cFpsWhole = fxtoi(this.s_cfxFPS);
      const cFpsFraction = fxtoi(mulfx(fracfx(this.s_cfxFPS), itofx(10)));

      // gfShowFPS — the live Test Options override (dbgOverlay.showFps) OR the
      // construction-time deps.fShowFPS. Write the canonical fps text holder
      // (Phase 1) when shown — the same SetText-only-when-visible lifecycle the
      // canvas control had (SimUI.cpp:1233). The DOM push reads s_fpsText
      // (no GetControlPtr for fps data).
      const fShowFps = dbgOverlay.showFps || (this.deps.fShowFPS ?? false);
      if (fShowFps) {
        this.s_fpsText = `${cFpsWhole}.${cFpsFraction}`;
      }

      this.s_tLastFPSUpdate = tCurrent;
      this.gcPaint = 0;

      // Set the # of pen events to accept per second. We don't need anything
      // faster than the frame rate.

      if (this.s_tLastPaint === 0) this.s_tLastPaint = tCurrent;
      this.deps.setPenEventInterval?.(tCurrent - this.s_tLastPaint);
      this.s_tLastPaint = tCurrent;
    }

    // (Phase 2 — forms.ini elimination): the canvas minimap's throttled self-invalidate
    // (gpmm->Update()) is gone — the DOM <canvas> radar re-renders per frame from the
    // coalesced content-dirty flag (gamesession.minimapModel), with no form to invalidate.

    // Everything has been updated for (at least) the first time
    // so we're ready to display it.

    if (!this.s_fReadyForPaint) {
      this.s_fReadyForPaint = true;
      this.InvalidateRect(null);
    }

    // Set redraw

    sui.evm.SetRedrawFlags(kfRedrawDirty);

    // The 4 in-game OVERLAY text labels flow from the CANONICAL holders (Phase 1) to
    // the DOM SimUIView (Phase 3 — the canvas controls are DELETED):
    //   objective — plrLocal.GetObjective()
    //   alert     — this.alertState {text, visible}
    //   countdown — this.countdownText / this.countdownVisible
    //   fps       — this.s_fpsText (written in the stats arm) + the gfShowFPS flag
    // DISPLAY-only — no sim/command/wire touch. The render-only host (node oracle /
    // replay viewer) leaves domSimUI absent → no overlay labels on the playfield
    // (they are HUD chrome, not playfield — the canvas no longer carries them).
    const fShowFpsNow = dbgOverlay.showFps || (this.deps.fShowFPS ?? false);
    if (this.deps.domSimUI !== undefined) {
      this.deps.domSimUI.update({
        objective: sui.ctx.plrLocal!.GetObjective(),
        alert: {
          text: this.alertState.text,
          visible: this.alertState.visible,
        },
        countdown: {
          text: this.countdownText,
          visible: this.countdownVisible,
        },
        fps: {
          text: this.s_fpsText,
          // gfShowFPS — the live Test Options override OR the construction-time flag.
          visible: fShowFpsNow,
        },
      });
    }
  }

  // game/SimUI.cpp:1272-1396 (the DRAW_LINES/DRAW_PATHS/tile-indicator debug
  // arms and the #if 0 minimap-opaque arm are compiled out)
  OnUpdateMapInvalidate(pupd: UpdateMap, prcOpaque: Rect): void {
    const sui = this.deps.sui;
    const c = sui.gctx.coords;

    // Debug overlays (render/debugoverlay.ts): force a full-frame repaint while
    // active so the moving path/target lines don't smear, plus one frame after a
    // toggle (dirty) so they appear/clear at once. This is where the C++ kept its
    // DRAW_LINES/DRAW_PATHS invalidation arms (see the comment above).
    if (dbgOverlay.paths || dbgOverlay.lines || dbgOverlay.dirty) {
      this.InvalidateRect(null);
      dbgOverlay.dirty = false;
    }

    // The placement overlay (hatch tiles + ✓/✗ buttons) draws UNGATED on every paint
    // (OnPaintSimUI / OnPaintControlsSimUI below) — the Palm-era C++ was safe because
    // paints never outran the placement OnTimer's per-tick invalidation, so every
    // painted frame recomposited the region in order (terrain → hatch → FOG over it).
    // Web paints CAN precede the timer within a tick (kfRedrawBeforeTimer input
    // paints, kfRedrawMax glide) — such a paint would re-draw the hatch OVER the
    // previous frame's fog: the idle flashing + fog-reveal cheat. Re-assert the
    // placement damage once per PAINT so the region always recomposites fully.
    const pfrmPlace = sui.host.GetPlaceForm();
    if (pfrmPlace !== null && pfrmPlace.IsBusy()) pfrmPlace.InvalidatePlacementRects?.();

    // Invalidate move target

    if (sui.m_nStateMoveTarget !== 0) {
      pupd.InvalidateTile(TcFromWc(sui.m_wxMoveTarget), TcFromWc(sui.m_wyMoveTarget));
      if (sui.m_nStateMoveTarget === 2) sui.m_nStateMoveTarget = 0;
    }

    // Handle selection invalidation

    this.InvalidateDragSelection();

    // Calculate the current opaquing rect for the map.
    // HACK: Store in a global since various places need it, such as when a gob
    // gets removed or when galaxite gets invalidated.

    const rcT = new Rect();
    const fEmpty = prcOpaque.IsEmpty();
    if (!fEmpty) {
      // Use the opaque rect passed

      rcT.SetRc(prcOpaque);
    }

    // Map it map relative in tile coords

    if (!fEmpty) {
      const wpt = { wx: 0, wy: 0 };
      sui.GetViewPos(wpt);
      const rcMapRelative = new Rect();
      rcMapRelative.SetRc(rcT);
      rcMapRelative.Offset(c.PcFromWc(wpt.wx), c.PcFromWc(wpt.wy));
      this.gtrcMapOpaque.txLeft = c.TcFromPc(rcMapRelative.left + c.gcxTile - 1);
      this.gtrcMapOpaque.tyTop = c.TcFromPc(rcMapRelative.top + c.gcyTile - 1);
      this.gtrcMapOpaque.txRight = c.TcFromPc(rcMapRelative.right);
      this.gtrcMapOpaque.tyBottom = c.TcFromPc(rcMapRelative.bottom);

      // Expand the tile rect if the opaque rect is on the screen
      // edge. This way gobs will get opaqued when on screen edge even

      const sizDib = this.m_pfrmm!.GetDib().GetSize();
      if (rcT.left <= 0) this.gtrcMapOpaque.txLeft = 0;
      if (rcT.top <= 0) this.gtrcMapOpaque.tyTop = 0;
      if (rcT.right >= sizDib.cx) this.gtrcMapOpaque.txRight = ktcMax;
      if (rcT.bottom >= sizDib.cy) this.gtrcMapOpaque.tyBottom = ktcMax;

      // Turn this opaque rect on by setting this global

      this.gptrcMapOpaque = this.gtrcMapOpaque;
    } else {
      this.gptrcMapOpaque = null;
    }

    // Get visible gobs

    const apgobVisible = sui.FindVisibleGobs(); // gsim.FindVisibleGobs

    // Damage-driven scoped invalidation on EVERY frame, at any gob count. The old
    // PR #257 code fell back to a full-frame repaint once the visible set passed a
    // count threshold, on the assumption that a dense battle's damage approaches the
    // whole screen and one repaint beats N scoped rects. Wall-clock measurement
    // disproved it: a full repaint's cost is dominated by re-blitting the ENTIRE
    // viewport terrain and is ~constant regardless of how many gobs moved, whereas
    // scoped blits only damaged terrain — so scoped is ~60% faster even when EVERY
    // visible gob redraws (c_05, 199 gobs: scoped ~21ms vs full ~52ms). Full repaint
    // only wins near ~100% damage coverage, which needs ~500-768 gobs (the engine's
    // 768 total-gob cap) packed into a single screen — unreachable in real play, where
    // units collision-spread as they maneuver (densest observed in a normal view: ~200).
    // The count-based trigger was also the wrong signal: with a fixed tile size a higher
    // gob count means a LARGER, more SPREAD-OUT view (lower coverage), so it fired
    // hardest exactly where the full repaint was most wasteful. Removing it is safe for
    // correctness because the draw list is complete (cap 768 = engine gob ceiling, so
    // FindVisibleGobs never truncates): every drawable gob is in the list, so the
    // departed-gob damage diff below is exhaustive. (View jumps still full-repaint via
    // GameRenderer's fScrolled path — separate from this.)

    if (this.m_apgobVisiblePrev !== apgobVisible) {
      // Gobs that LEFT the visible set since the previous paint still have last-painted
      // pixels on screen — damage their last bounds. The kfGobIncludeFindVisible flag
      // covers a gob stepping into the walk window's ±1-cell margin, but NOT one whose
      // cell crosses beyond the margin in a single update (fast movers, top/left edge
      // feet-in-view cases); this diff damages those strands. FindVisibleGobs caches by
      // (view, updateCount), so an unchanged frame returns the SAME array and is skipped.
      if (this.m_apgobVisiblePrev !== null) {
        const cur = this.m_setVisibleScratch;
        cur.clear();
        for (const pgob of apgobVisible) cur.add(pgob);
        for (const pgobPrev of this.m_apgobVisiblePrev) {
          if (cur.has(pgobPrev)) continue;
          const trc = (pgobPrev as GobWithBounds).m_trcBoundingLast;
          if (trc !== undefined) pupd.InvalidateMapTileRect(trc);
        }
      }
      this.m_apgobVisiblePrev = apgobVisible;
    }

    // Credits-derived presentation: the Warehouse fill art and the selected
    // processor/warehouse pip rows draw from the LIVE player credits/capacity
    // (gobdraw creditsPips) — values that change with NO gob-side damage (any
    // producer/consumer anywhere moves them; Player.SetCredits marks nothing).
    // Under the old full repaint this was invisible; under scoped invalidation a
    // visible warehouse would keep its stale fill state across a 30%-of-capacity
    // boundary. Per-paint value diff: stash the last-rendered pip value on the
    // gob (render-side, like m_trcBoundingLast) and re-mark when it moves —
    // BEFORE the kfGobRedraw scan below so the damage lands this paint.
    for (const pgob of apgobVisible) {
      const gt = pgob.GetType();
      if (gt !== kgtWarehouse && (gt !== kgtProcessor || !(pgob.GetFlags() & kfGobSelected))) {
        continue;
      }
      const g = pgob as unknown as {
        m_pplr?: { GetCredits(): number; GetCapacity(): number };
        m_nPipsLastRender?: number;
        MarkRedraw(): void;
      };
      if (g.m_pplr === undefined) continue;
      const nPips = creditsPips(g.m_pplr);
      if (g.m_nPipsLastRender !== nPips) {
        g.m_nPipsLastRender = nPips;
        g.MarkRedraw();
      }
    }

    // COSMETIC projectile interpolation: advance each visible projectile's interp draw
    // position and flag the ones that moved kfGobRedraw, BEFORE the invalidate loop below
    // (which then damages old+new via shotClippingBounds reading the SAME interp pos).
    // Render-only — never touches sim/gob state. See render/projectile-interp.ts.
    // every frame is scoped now (no full-repaint fallback), so projectile interp is
    // always active: the marking pass re-marks the interpolated shots in the cells it
    // damages via the same complete visible list.
    beginProjectileInterp(
      apgobVisible as unknown as Parameters<typeof beginProjectileInterp>[0],
      this.deps.HostGetTickCount(),
      true,
    );

    // Add known invalid gobs to the update map

    for (const pgob of apgobVisible) {
      // Invalidate if asked to redraw

      if (pgob.GetFlags() & kfGobRedraw) {
        gobInvalidate(sui.gctx, pupd, pgob as GobWithBounds, this.gptrcMapOpaque);
      }
    }

    // Mark gobs not already marked for redraw that are touching invalid areas
    // Add this "overflow" to the damage map; this is what is used to create
    // a valid back buffer when scrolling

    let fNewInvalid = { v: false };
    do {
      fNewInvalid = { v: false };
      for (const pgob of apgobVisible) {
        const ff = pgob.GetFlags();
        if (!(ff & kfGobRedraw)) {
          const g = pgob as GobWithBounds;
          if (g.m_trcBoundingLast === undefined) {
            g.m_trcBoundingLast = { txLeft: 127, tyTop: 127, txRight: -128, tyBottom: -128 };
          }
          if (pupd.IsMapTileRectInvalidAndTrackDamage(g.m_trcBoundingLast, fNewInvalid)) {
            pgob.SetFlags((ff | kfGobRedraw) >>> 0);
          }
        }
      }
    } while (fNewInvalid.v);

    // (Phase 3) no control tree → the base Form's per-control invalidation pass
    // (form.cpp:287-304) is gone; the gob-dirty marking above is the whole job.
  }

  // game/SimUI.cpp:1398-1446
  InvalidateDragSelection(): void {
    if (this.drag.fDragSelecting) {
      const sui = this.deps.sui;
      const c = sui.gctx.coords;
      const wpt = { wx: 0, wy: 0 };
      sui.GetViewPos(wpt);
      const wxView = wpt.wx;
      const wyView = wpt.wy;
      const gpupdSim = this.m_pfrmm!.GetUpdateMap()!;

      if (this.drag.fLassoSelection) {
        // The slow redraw this causes is unavoidable unless we remove lasso
        // as an option.

        let wptLastX = this.drag.awptSelection[0].wx - wxView;
        let wptLastY = this.drag.awptSelection[0].wy - wyView;
        for (let i = 1; i < this.drag.cwptSelection; i++) {
          const wptNextX = this.drag.awptSelection[i].wx - wxView;
          const wptNextY = this.drag.awptSelection[i].wy - wyView;
          const x1 = c.PcFromWc(wptLastX);
          const y1 = c.PcFromWc(wptLastY);
          const x2 = c.PcFromWc(wptNextX);
          const y2 = c.PcFromWc(wptNextY);
          const rcT = new Rect();
          rcT.left = Math.min(x1, x2);
          rcT.top = Math.min(y1, y2);
          rcT.right = rcT.left + Math.abs(x2 - x1) + 1;
          rcT.bottom = rcT.top + Math.abs(y2 - y1) + 1;
          gpupdSim.InvalidateRect(rcT);
          wptLastX = wptNextX;
          wptLastY = wptNextY;
        }
      } else {
        // PERF: there is a way to make selection drawing faster by only
        // invalidating edges that move, and redrawing dirty cells only on the
        // control layer. Can do if this isn't fast enough.

        const xLeft = c.PcFromWc(this.drag.wrcSelection.left - wxView);
        const yTop = c.PcFromWc(this.drag.wrcSelection.top - wyView);
        const xRight = c.PcFromWc(this.drag.wrcSelection.right - wxView);
        const yBottom = c.PcFromWc(this.drag.wrcSelection.bottom - wyView);

        const rcT = new Rect();
        rcT.Set(xLeft, yTop, xRight, yTop + 1);
        gpupdSim.InvalidateRect(rcT);
        rcT.Set(xRight - 1, yTop, xRight, yBottom);
        gpupdSim.InvalidateRect(rcT);
        rcT.Set(xLeft, yBottom - 1, xRight, yBottom);
        gpupdSim.InvalidateRect(rcT);
        rcT.Set(xLeft, yTop, xLeft + 1, yBottom);
        gpupdSim.InvalidateRect(rcT);
      }
    }
  }

  // game/SimUI.cpp:1448-1453
  ScrollInvalidate(_pupd: UpdateMap | null): void {
    // (Phase 3) no control tree → the base Form's per-control scroll-invalidate
    // (form.cpp:344-350) is gone; only the drag-selection rect needs re-marking.
    this.InvalidateDragSelection();
    // (Phase 2 — forms.ini elimination): no canvas minimap form to invalidate on scroll;
    // the DOM radar's white viewport box re-renders each frame and tracks the scroll.
  }

  // game/SimUI.cpp:1455-1461
  OnPaintBackground(pbm: CpuDibBitmap, pupd: UpdateMap | null): void {
    if (!this.s_fReadyForPaint) return;

    // gsim.DrawBackground(pupd, pbm)
    drawBackground(
      this.deps.sui.gctx,
      this.refreshView(),
      this.deps.tmap,
      this.deps.fog,
      pupd!,
      pbm,
    );
  }

  // game/SimUI.cpp:1463-1514
  OnPaint(pbm: CpuDibBitmap): void {
    // We're not ready to paint until the first Update has executed because
    // Update initializes things like the Credits and Power controls.

    if (!this.s_fReadyForPaint) return;

    const sui = this.deps.sui;
    const c = sui.gctx.coords;

    // Draw everything managed by the Simulation (map, Gobs, Gob UI).
    // gsim.Draw consumes the SAME FindVisibleGobs cache OnUpdateMapInvalidate
    // marked (Simulation.cpp:806 — one m_apgobVisible list per frame); a
    // second, larger walk here would draw gobs the invalidation pass never
    // re-marks, and the per-frame background repaint then erases them for good.
    simulationDraw(
      sui.gctx,
      this.refreshView(),
      this.m_pfrmm!.GetUpdateMap()!,
      pbm,
      sui.FindVisibleGobs() as unknown as Parameters<typeof simulationDraw>[4],
    );

    // Developer debug overlays (Alt+L / Alt+P; render/debugoverlay.ts) — ports of
    // the C++ DRAW_LINES (Simulation.cpp:882) and DRAW_PATHS (Simulation.cpp:810)
    // Test Options arms. Off by default; drawn over the gobs like the lasso below.
    if (dbgOverlay.lines) drawDebugTargetLines(sui, pbm);
    if (dbgOverlay.paths) drawDebugPaths(sui, pbm);

    // Draw the selection rectangle or lasso lines, if any

    if (this.drag.fDragSelecting) {
      const wpt = { wx: 0, wy: 0 };
      sui.GetViewPos(wpt);
      const wxView = wpt.wx;
      const wyView = wpt.wy;

      if (this.drag.fLassoSelection) {
        const clr = GetColor(iclr.kiclrWhite);
        let wptLastX = this.drag.awptSelection[0].wx - wxView;
        let wptLastY = this.drag.awptSelection[0].wy - wyView;
        for (let i = 1; i < this.drag.cwptSelection; i++) {
          const wptNextX = this.drag.awptSelection[i].wx - wxView;
          const wptNextY = this.drag.awptSelection[i].wy - wyView;
          pbm.DrawLine(
            c.PcFromWc(wptLastX),
            c.PcFromWc(wptLastY),
            c.PcFromWc(wptNextX),
            c.PcFromWc(wptNextY),
            clr,
          );
          wptLastX = wptNextX;
          wptLastY = wptNextY;
        }
      } else {
        const rcT = new Rect();
        rcT.left = c.PcFromWc(this.drag.wrcSelection.left - wxView);
        rcT.top = c.PcFromWc(this.drag.wrcSelection.top - wyView);
        rcT.right = c.PcFromWc(this.drag.wrcSelection.right - wxView);
        rcT.bottom = c.PcFromWc(this.drag.wrcSelection.bottom - wyView);
        DrawBorder(pbm, rcT, 1, GetColor(iclr.kiclrWhite));
      }
    }

    // Handle placement form

    sui.host.GetPlaceForm()?.OnPaintSimUI?.(pbm); // gpfrmPlace->OnPaintSimUI

    // Let the input handler draw what it needs

    this.m_ppenh.OnPaint(pbm);

    // (Phase 3) no control tree → the base Form's blt-bitmap OnPaint is gone.
  }

  // game/SimUI.cpp:1516-1601 (the STATS_DISPLAY arms are compiled out)
  OnPaintControls(pbm: CpuDibBitmap, pupd: UpdateMap | null): void {
    // We're not ready to paint until the first Update has executed because
    // Update initializes things like the Credits and Power controls.

    if (!this.s_fReadyForPaint) return;

    const sui = this.deps.sui;
    const host = this.m_pfrmm!.host;

    // Give some time to sound servicing

    host.hostSoundServiceProc();

    // Draw fog

    drawFog(sui.gctx, this.refreshView(), this.deps.fog, pupd!, pbm); // gsim.DrawFog

    // Draw move indicator

    if (sui.m_nStateMoveTarget === 1) {
      const c = sui.gctx.coords;
      const wpt = { wx: 0, wy: 0 };
      sui.GetViewPos(wpt);
      // m_aniMoveTarget.Draw (ht.h:1783) -> AnimationData::DrawFrame
      const anid = sui.gctx.anidFor('movetarget.anir');
      if (anid !== null) {
        // Attack-move targets reuse the same flourish but draw red-tinted to differentiate
        // from a plain move (ksideAttackMoveTint); plain moves draw neutral.
        anid.DrawFrame(
          this.m_aniMoveTarget.GetStrip(),
          this.m_aniMoveTarget.GetFrame(),
          pbm,
          c.PcFromUwc(sui.m_wxMoveTarget) - c.PcFromWc(wpt.wx),
          c.PcFromUwc(sui.m_wyMoveTarget) - c.PcFromWc(wpt.wy),
          sui.m_fMoveTargetAttack ? ksideAttackMoveTint : 0 /* ksideNeutral */,
        );
      }
    }

    // Give some time to sound servicing

    host.hostSoundServiceProc();

    // (Phase 3) no control tree → the base Form's per-control paint is gone.

    // Handle Structure Placement controls here

    sui.host.GetPlaceForm()?.OnPaintControlsSimUI?.(pbm, pupd); // gpfrmPlace
  }

  // game/SimUI.cpp — the engine's per-frame layer hooks (no control tree → no-ops
  // beyond what the sim draw already does)
  FrameStart(): void {}

  // game/SimUI.cpp:1603-1606
  FrameComplete(): void {
    // (Phase 3) no control tree → nothing per-frame to finalize.
  }

  // game/SimUI.cpp:1631-1671 (the WIN/DEBUG hover-trace arm is compiled out).
  // (Phase 3) the chat button (kidcChat → showChat) is a DOM control now; there
  // is no canvas control tree to give "first crack" at the pen event.
  OnPenEvent(pevt: Event): boolean {
    // Debug gob identification

    if (pevt.eType === penHoverEvent) {
      return true;
    }

    const sui = this.deps.sui;
    let fScrollOnly = false;
    const gpfrmPlace = sui.host.GetPlaceForm();
    if (gpfrmPlace !== null && gpfrmPlace.IsBusy()) {
      fScrollOnly = true;
    }
    if (sui.ctx.plrLocal!.GetFlags() & kfPlrObserver) {
      fScrollOnly = true;
    }

    return this.m_ppenh.OnPenEvent(pevt, fScrollOnly);
  }

  // game/SimUI.cpp ~SimUIForm — drop the canonical alert's 4s timer if still armed
  // (the AlertControl's ~ctor did this, alertcontrol.cpp:36-40; now the AlertState
  // owns it) so it doesn't dangle on the gtimm after teardown, then unregister the
  // layer from its FormMgr (no control tree to dispose).
  Dispose(): void {
    this.alertState.Dispose();
    if (this.m_pfrmm !== null) this.m_pfrmm.RemoveForm(this);
  }

  // --- PaintLayer scaffolding the engine reads (the Form base provided these;
  // the layer supplies them directly now — no forms.ini, no control tree) ---
  GetId(): number {
    return this.m_idf;
  }
  GetFlags(): number {
    return this.m_wf;
  }
  GetFormMgr(): FormMgr | null {
    return this.m_pfrmm;
  }
  GetRect(prc: Rect): void {
    prc.SetRc(this.m_rc);
  }
  SetRect(prc: Rect): void {
    this.InvalidateRect(null);
    this.m_rc.SetRc(prc);
    this.InvalidateRect(null);
  }
  // form.cpp:487-499 — route to the FormMgr's UpdateMap (null = the whole rect)
  InvalidateRect(prc: Rect | null): void {
    if (this.m_pfrmm === null) return;
    this.m_pfrmm.InvalidateRect(prc ?? this.m_rc);
  }
  // form.cpp:512-547 OnHitTest — the playfield is full-bleed; a pointer inside the
  // sim rect hits it (no per-control hit-test, which is what the finger arm did).
  OnHitTest(pevt: Event): boolean {
    if (!(this.m_wf & kfFrmVisible)) return false;
    return this.m_rc.PtIn(pevt.x, pevt.y);
  }
  // form.cpp:549-567 OnKeyTest — focusable (no kfFrmNoFocus): the in-game layer is
  // the key target when no modal form is up (the FormMgr.GetFocus walk).
  OnKeyTest(_pevt: Event | null): boolean {
    return true;
  }
  // form.cpp:352-355 — the layer has no scroll-time bookkeeping of its own
  OnScroll(_dx: number, _dy: number): void {}
  // form.cpp:503-510 — no control capture to break on this layer
  BreakCapture(): void {}
}
